import { Box, IconButton, Paper, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { HedisMetricStatusEnum } from '../../../Enum/HedisMetricStatusEnum';
import { HedisMetricsTypeEnum } from '../../../Enum/HedisMetricsTypeEnum';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { fontColor } from '../MemberInformation/MemberInformation';
import { hedisMetricsList } from '../../../store/memberDemographicsDetails.slice';
import InfoIcon from '@mui/icons-material/Info';
import React, { useState } from 'react';
import { formatUtcDate } from '../../../utils/timeFormat';
import ScienceIcon from '@mui/icons-material/Science';
import { HedisMetricsVisit } from '../../../Models/HedisMetrics/HedisMetricsVisit.model';

interface MemberHedisTilesProps {
    setHedisDetail: React.Dispatch<React.SetStateAction<HedisMetricsVisit | null>>;
    setHedisType: React.Dispatch<React.SetStateAction<string>>;
    setShowHedisDetail: React.Dispatch<React.SetStateAction<boolean>>;
}

const MemberHedisTiles = (props: MemberHedisTilesProps) => {
    const { setHedisDetail, setHedisType, setShowHedisDetail } = props;
    const { chartDetails } = useSelector((state: RootState) => state.memberDemographicsDetails);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedTooltipHedisType, setSelectedTooltipHedisType] = useState<string>('');

    const prepareHedis = (metric: HedisMetricsVisit, hedisType: string) => {
        setHedisDetail(metric);
        setHedisType(hedisType);
        setShowHedisDetail(true);
    };

    const prepareHedisDetail = () => {
        if (Boolean(chartDetails?.HedisSummary)) {
            var hedisDetailList = { controlledList: [], notControlledList: [], notApplicableList: [], applicableYetToStartList: [] };
            hedisMetricsList.forEach((metric, index) => {
                let metricInfo = chartDetails.HedisSummary.find((h) => h.HedisMetricType === metric.type);
                let metricInfoCopy = {
                    ...metricInfo,
                    Label: metric.label,
                    Type: metric.type,
                    FontColor: fontColor(metricInfo),
                    IsShow: false,
                    ToolInfo: prepareToolInfo(metric.type),
                };
                if (metricInfo?.IsApplicable) {
                    if (
                        metricInfoCopy?.MetricStatus === HedisMetricStatusEnum.Completed ||
                        metricInfoCopy?.MetricStatus === HedisMetricStatusEnum.CompletedAndControlled
                    ) {
                        hedisDetailList.controlledList.push(metricInfoCopy);
                    } else if (metricInfoCopy?.MetricStatus === HedisMetricStatusEnum.CompletedAndNotControlled) {
                        hedisDetailList.notControlledList.push(metricInfoCopy);
                    } else {
                        hedisDetailList.applicableYetToStartList.push(metricInfoCopy);
                    }
                } else {
                    hedisDetailList.notApplicableList.push(metricInfoCopy);
                }
            });
            return [
                {
                    Name: 'Not Applicable',
                    InfoText: 'Not Applicable',
                    List: hedisDetailList.notApplicableList,
                },
                {
                    Name: 'Applicable And Not Controlled',
                    InfoText: 'Applicable And Not Controlled/Not Met',
                    List: hedisDetailList.notControlledList,
                },
                {
                    Name: 'Applicable And Controlled',
                    InfoText: 'Applicable And Completed/Controlled',
                    List: hedisDetailList.controlledList,
                },
                {
                    Name: 'Applicable And Yet To Performed',
                    InfoText: 'Applicable And Yet To Performed',
                    List: hedisDetailList.applicableYetToStartList,
                },
            ].filter((h) => h.List.length);
        }
    };

    const prepareToolInfo = (type: number) => {
        switch (type) {
            case HedisMetricsTypeEnum.BP:
                return (
                    <div>
                        <Typography variant="body2">Member who has an ICD 10 of I10, within a rolling 12 months</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Controlled BP :
                        </Typography>

                        <Typography variant="caption">
                            CPT Codes 3074F or 3075F Systolic (must have one or the other of these) and 3078F or 3079F Diastolic (must have one or the other of
                            these)
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Controlled Bp :
                        </Typography>
                        <Typography variant="caption">CPT Codes 3077F and 3080F</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Documented :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>
                    </div>
                );
            case HedisMetricsTypeEnum.ProviderVisits:
                return (
                    <div>
                        <Typography variant="body2">Member is 20 years of age or older, within a rolling 12 months</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Visits Completed :
                        </Typography>

                        <Typography variant="caption">
                            Any of the CPT codes - 92002, 92004, 92012, 92014, 93005, 97597, 98966, 98967, 98968, 98969, 98970, 98971, 98972, 99211, 99212,
                            99213, 99214, 99215, 99201, 99202, 99203, 99204, 99205, 99223, 99241, 99242, 99243, 99244, 99245, 99281, 99282, 99283, 99284, 99285,
                            99304, 99305, 99306, 99307, 99308, 99309, 99310, 99334, 99335, 99336, 99337, 99341, 99342, 99343, 99344, 99345, 99347, 99348, 99349,
                            99350, 99381, 99382, 99383, 99384, 99385, 99386, 99387, 99391, 99392, 99393, 99394, 99395, 99396, 99397, 99401, 99402, 99403, 99404,
                            99411, 99412, 99421, 99422, 99423, 99429, 99441, 99442, 99443, 99444, 99457, 99483, G0402, G0438, G0439, G0463, T1015, H1000, G0071,
                            G2010, G2012, G2061, G2062, G2063, G2250, G2251, G2252
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            No Visits :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>
                    </div>
                );
            case HedisMetricsTypeEnum.BMI:
                return (
                    <div>
                        <Typography variant="body2">For all adults 18-74, within a rolling 12 months</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            BMI Completed :
                        </Typography>

                        <Typography variant="caption">
                            Any of the ICD 10 codes - Z38.20, Z68, Z68.1, Z68.2, Z68.20, Z68.21, Z68.22, Z68.23, Z68.24, Z68.25, Z68.26, Z68.27, Z68.28, Z68.29,
                            Z68.3, Z68.30, Z68.31, Z68.32, Z68.33, Z68.34, Z68.35, Z68.36, Z68.37, Z68.38, Z68.39, Z68.4, Z68.41, Z68.42, Z68.43, Z68.44,
                            Z68.45, Z68.5, Z68.51, Z68.52, Z68.53, Z68.54
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            No BMI Documented :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>
                    </div>
                );
            case HedisMetricsTypeEnum.BreastCancerScreening:
                return (
                    <div>
                        <Typography variant="body2">
                            For females 50-74 years of age, screened within the past two years
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Screened :
                        </Typography>

                        <Typography variant="caption">
                            Any of the CPT codes - 77055, 77056, 77057, 77061, 77062, 77065, 77066, 77067, G0202, G0204, G0206
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Screened :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>
                    </div>
                );
            case HedisMetricsTypeEnum.HgbA1c:
                return (
                    <div>
                        <Typography variant="body2">For members aged 18-75 who have an ICD 10 of E10-E11</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Controlled :
                        </Typography>
                        <Typography variant="caption">Any of the CPT codes 3044F or 3051F</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Controlled :
                        </Typography>
                        <Typography variant="caption">Any of the CPT codes 3046F or 3052F</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Documented :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>
                    </div>
                );
            case HedisMetricsTypeEnum.DiabeticEyeExam:
                return (
                    <div>
                        <Typography variant="body2">
                            For members who have an ICD 10 of E10-E11 and are between the ages of 18-75 in the rolling 12 month time frame
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Screened :
                        </Typography>

                        <Typography variant="caption">
                            Any of the CPT codes 67028, 67030, 67031, 67036, 67039, 67040, 67041, 67042, 67043, 67101, 67105, 67107, 67108, 67110, 67113, 67121,
                            67141, 67145, 68208, 67210, 67218, 67220, 67221, 67227, 67228, 92002, 92004, 92012, 92014, 92018, 92019, 92134, 92225, 92226, 92227,
                            92228, 92230, 92235, 92240, 92550, 92260,99203, 99204, 99205, 99213, 99214, 99215, 99242, 99243, 99244, 99245
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Screened :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>
                    </div>
                );
            case HedisMetricsTypeEnum.MedicationReconciliation:
                return (
                    <div>
                        <Typography variant="body2">Within 30 days of discharge from a facility</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Documented :
                        </Typography>

                        <Typography variant="caption">CPT code 1111F</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Documented :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>
                    </div>
                );
            case HedisMetricsTypeEnum.DepressionScreening:
                return (
                    <div>
                        <Typography variant="body2">For members aged 18-75, in the rolling 12 month time frame</Typography>
                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Met :
                        </Typography>
                        <Typography variant="caption">CPT code G8510 OR G8431 OR G8511</Typography>
                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Met :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>
                    </div>
                );
            case HedisMetricsTypeEnum.DepressionFollowUp:
                return (
                    <div>
                        <Typography variant="body2">For members aged 18-75 within the rolling 12 month time frame</Typography>
                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Met :
                        </Typography>

                        <Typography variant="caption">
                            Had an encounter with a CPT code of G8431 within 30 days of the G8511 CPT code being entered, or if G8431 is the more recent CPT
                            code.
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Met :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>
                    </div>
                );
            case HedisMetricsTypeEnum.LipidForBhDx:
                return (
                    <div>
                        <Typography variant="body2">
                            For members aged 18-64 who has an ICD 10 of F20.9, F25.0, F25.9, F31.32, F31.2, F31.3, F31.81, F31.89, F31.9
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Controlled :
                        </Typography>

                        <Typography variant="caption">CPT code 3048F</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Controlled :
                        </Typography>
                        <Typography variant="caption">Any of the CPT codes 3049F or 3050F</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Documented :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>
                    </div>
                );
            case HedisMetricsTypeEnum.HgbA1cForBhDx:
                return (
                    <div>
                        <Typography variant="body2">
                            For members aged 18-64 who has an ICD 10 of F20.9, F25.0, F25.9, F31.32, F31.2, F31.3, F31.81, F31.89, F31.9
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Controlled :
                        </Typography>

                        <Typography variant="caption">Any of the CPT codes 3044F or 3051F</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Controlled :
                        </Typography>
                        <Typography variant="caption">Any of the CPT codes 3046F or 3052F</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Documented :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>
                    </div >
                );
            case HedisMetricsTypeEnum.CervicalCancerScreening:
                return (
                    <div>
                        <Typography variant="body2">
                            For females 21-64 years of age, if one of the following criteria is met, the cancer screening metric is considered to be met
                        </Typography>
                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Screened :
                        </Typography>
                        <Typography variant="caption" display="block">
                            Last Met based on the external claim source in the last 2 years
                        </Typography>
                        <Typography variant="caption">
                            Member's age 21-64 with any of the CPT codes - 88141, 88142, 88143, 88147, 88148, 88150, 88152, 88153, 88164, 88165, 88166, 88167,
                            88174, 88175, or HCPCS G0123, G0124, G0141, G0143, G0144, G0145, G0147, G0148, P3000, P3001, Q0091 within the last 3 years
                        </Typography>
                        <Typography variant="caption" display="block">
                            Member's age 30-64 with any of the CPT codes - 87624, 87625, 88141, 88142, 88143, 88147, 88148, 88150, 88152, 88153, 88164, 88165,
                            88166, 88167, 88174, 88175, or HCPCS G0123, G0124, G0141, G0143, G0144, G0145, G0147, G0148, G0476, P3000, P3001, Q0091 within the
                            last 5 years
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Screened :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Exclusions :
                        </Typography>
                        <Typography variant="caption">Female age 65 or older</Typography>
                        <Typography variant="caption" display="block">
                            CPT: 57530, 57531, 57540, 57545, 57550, 57555, 57556, 58150, 58152, 58200, 58210, 58240, 58260, 58262, 58263, 58267, 58270, 58275,
                            58280, 58285, 58290, 58291, 58292, 58293, 58294, 58548, 58550, 58552, 58553, 58554, 58570, 58571, 58572, 58573, 58575, 58951, 58953,
                            58954, 58956, 59135, 99377, 99378
                        </Typography>
                        <Typography variant="caption" display="block">
                            HCPCS: G0182, G9473-G9479, Q5003-Q5008, Q5010, S9126, T2042-T2046, G9054, M1017
                        </Typography>
                        <Typography variant="caption" display="block">
                            ICD10: Q51.5, Z90.710, Z90.712, 0UTC0ZZ, 0UTC4ZZ, 0UTC7ZZ, 0UTC8ZZ
                        </Typography>
                    </div>
                );
            case HedisMetricsTypeEnum.ColorectalCancerScreening:
                return (
                    <div>
                        <Typography variant="body2">
                            For members 45-75 years of age, if one of the following criteria is met, the cancer screening metric is considered to be met
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Screened :
                        </Typography>
                        <Typography variant="caption" display="block">
                            Last Met based on the external claim source in the last 2 years
                        </Typography>
                        <Typography variant="caption">CPT 81528 within the measurement year or the 2 years prior to the measurement year</Typography>
                        <Typography variant="caption" display="block">
                            CPT 82270, 82274 or HCPCS G0328 within the measurement year
                        </Typography>
                        <Typography variant="caption" display="block">
                            CPT 45330, 45331, 45332, 45333, 45334, 45335, 45337, 45338, 45340, 45341, 45342, 45346, 45347, 45349, 45350 or HCPCS G0104 within
                            the measurement year or the 4 years prior to the measurement year
                        </Typography>
                        <Typography variant="caption" display="block">
                            CPT 44388, 44389, 44390, 44391, 44392, 44393, 44394, 44401, 44402, 44403, 44404, 44405, 44406, 44407, 44408, 45378, 45379, 45380,
                            45381, 45382, 45383, 45384, 45385, 45386, 45387, 45388, 45389, 45390, 45391, 45392, 45393, 45398 or HCPCS G0105 or G0121 within the
                            measurement year or the 9 years prior to the measurement year
                        </Typography>
                        <Typography variant="caption" display="block">
                            CPT 74261, 74262, 74263 in the measurement year or the 4 years prior to the measurement year
                        </Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Not Screened :
                        </Typography>
                        <Typography variant="caption">No CPT codes recorded</Typography>

                        <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                            Exclusions :
                        </Typography>
                        <Typography variant="caption" display="block">
                            ICD10: C18.0, C18.1, C18.2, C18.3, C18.4, C18.5, C18.6, C18.7, C18.8, C18.9, C19, C20, C21.2, C21.8, C78.5, Z85.038, Z85.048,
                            ODTE0ZZ, ODTE4ZZ, ODTE7ZZ, ODTE8ZZ
                        </Typography>
                        <Typography variant="caption" display="block">
                            CPT: 44150, 44151, 44152, 44153, 44155, 44156, 44157, 44158, 44210, 44211, 44212
                        </Typography>
                    </div>
                );
        }
    };

    const hedisDetails = prepareHedisDetail();

    return (
        <Stack direction={isMobile ? 'column' : 'row'} spacing={1} marginTop={1} justifyContent={'center'} alignItems={isMobile ? 'stretch' : ''}>
            {hedisDetails?.map((metric: any, index) => (
                <Stack
                    direction="column"
                    key={`group-${index}`}
                    sx={{
                        borderRight: isMobile && index === hedisDetails.length - 1 ? 'none' : '2px solid #9E9E9E',
                        borderBottom: isMobile && index !== hedisDetails.length - 1 ? '2px solid #9E9E9E' : 'none',
                        minWidth: isMobile ? '100%' : 'auto',
                    }}
                >
                    <Typography variant="body2" sx={{ textAlign: 'center', color: '#7c7c7c' }}>
                        {metric.Name}
                        <Tooltip
                            disableHoverListener
                            onClose={() => setSelectedTooltipHedisType('')}
                            open={metric.InfoText === selectedTooltipHedisType}
                            sx={{ cursor: 'pointer' }}
                            title={metric.InfoText}
                            PopperProps={{ style: { zIndex: 1000 } }}
                        >
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTooltipHedisType(metric.InfoText);
                                }}
                                sx={{ paddingTop: '0px', paddingBottom: '0px', cursor: 'pointer' }}
                            >
                                <InfoIcon sx={{ width: '18px', height: '18px', paddingTop: '0px', paddingBottom: '0px' }} />
                            </IconButton>
                        </Tooltip>
                    </Typography>
                    <Stack
                        direction={isSmallScreen ? 'column' : 'row'}
                        columnGap={2}
                        p={1}
                        pl={0}
                        flexWrap="wrap"
                        rowGap={2}
                        justifyContent="center"
                        alignItems={isSmallScreen ? 'stretch' : 'flex-start'}
                    >
                        {metric.List.map((hedis, index) => {
                            const color = hedis.FontColor;
                            return Boolean(hedis.IsApplicable && (hedis.IsExpired || hedis.MetricStatus === HedisMetricStatusEnum.Expired)) ? (
                                <Paper
                                    elevation={3}
                                    key={index}
                                    sx={{
                                        position: 'relative',
                                        padding: '15px',
                                        textAlign: 'center',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        Boolean(hedis?.DateLastMet) && prepareHedis(hedis, hedis.Label);
                                    }}
                                >
                                    {/* Ribbon */}
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            position: 'absolute',
                                            top: '8px',
                                            left: '-22px',
                                            backgroundColor: '#d32f2f',
                                            color: 'white',
                                            padding: '3px 20px',
                                            transform: 'rotate(-45deg)',
                                            zIndex: 1,
                                            fontSize: '11px',
                                            lineHeight: '1',
                                        }}
                                    >
                                        Expired
                                    </Typography>
                                    <Typography fontWeight={700} color={color} variant="body1" pb={0.5}>
                                        {hedis.Label}
                                        <Tooltip
                                            disableHoverListener
                                            onClose={() => setSelectedTooltipHedisType('')}
                                            sx={{ cursor: 'pointer' }}
                                            open={hedis?.Type === selectedTooltipHedisType}
                                            title={hedis?.ToolInfo}
                                            PopperProps={{ style: { zIndex: 1000 } }}
                                        >
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedTooltipHedisType(hedis.Type);
                                                }}
                                                sx={{ paddingTop: '0px', paddingBottom: '0px', cursor: 'pointer' }}
                                            >
                                                <InfoIcon sx={{ width: '18px', height: '18px', paddingTop: '0px', paddingBottom: '0px', color: color }} />
                                            </IconButton>
                                        </Tooltip>
                                        {Boolean(hedis?.HedisAlert) ? (
                                            <IconButton
                                                aria-label="delete"
                                                sx={{ width: '18px', height: '18px', paddingTop: '0px', paddingBottom: '0px', color: color }}
                                            >
                                                <ScienceIcon />
                                            </IconButton>
                                        ) : null}
                                    </Typography>
                                    <Box color={color}>
                                        <Typography color={color} variant="body2" sx={{ whiteSpace: isSmallScreen ? 'normal' : 'nowrap' }}>
                                            {hedis?.DateLastMet && !hedis?.IsMetAtThisVisit ? 'Last Not Met : ' : 'Last Met : '}
                                        </Typography>
                                        <Typography color={color} variant="body2" sx={{ whiteSpace: isSmallScreen ? 'normal' : 'nowrap' }}>
                                            {formatUtcDate(hedis?.DateLastMet)}
                                        </Typography>
                                    </Box>
                                </Paper>
                            ) : (
                                <Paper
                                    elevation={3}
                                    key={index}
                                    sx={{
                                        textAlign: 'center',
                                        p: isSmallScreen ? 1.2 : 1.7,
                                        borderLeftWidth: 4,
                                        borderLeftColor: color,
                                        borderLeftStyle: 'solid',
                                        cursor: Boolean(hedis?.DateLastMet) ? 'pointer' : '',
                                        minWidth: isSmallScreen ? '100%' : 'auto',
                                    }}
                                    onClick={() => {
                                        Boolean(hedis?.DateLastMet) && prepareHedis(hedis, hedis.Label);
                                    }}
                                >
                                    <Typography fontWeight={700} color={color} variant="body1" pb={0.5}>
                                        {hedis.Label}
                                        <Tooltip
                                            disableHoverListener
                                            onClose={() => setSelectedTooltipHedisType('')}
                                            sx={{ cursor: 'pointer' }}
                                            open={hedis?.Type === selectedTooltipHedisType}
                                            title={hedis?.ToolInfo}
                                            PopperProps={{ style: { zIndex: 1000 } }}
                                        >
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedTooltipHedisType(hedis.Type);
                                                }}
                                                sx={{ paddingTop: '0px', paddingBottom: '0px', cursor: 'pointer' }}
                                            >
                                                <InfoIcon sx={{ width: '18px', height: '18px', paddingTop: '0px', paddingBottom: '0px', color: color }} />
                                            </IconButton>
                                        </Tooltip>
                                        {Boolean(hedis?.HedisAlert) ? (
                                            <IconButton
                                                aria-label="delete"
                                                sx={{ width: '18px', height: '18px', paddingTop: '0px', paddingBottom: '0px', color: color }}
                                            >
                                                <ScienceIcon />
                                            </IconButton>
                                        ) : null}
                                    </Typography>
                                    <Box color={color}>
                                        <Typography color={color} variant="body2" sx={{ whiteSpace: isSmallScreen ? 'normal' : 'nowrap' }}>
                                            {hedis?.DateLastMet && !hedis?.IsMetAtThisVisit ? 'Last Not Met : ' : 'Last Met : '}
                                        </Typography>
                                        <Typography color={color} variant="body2" sx={{ whiteSpace: isSmallScreen ? 'normal' : 'nowrap' }}>
                                            {formatUtcDate(hedis?.DateLastMet)}
                                        </Typography>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Stack>
                </Stack>
            ))}
        </Stack>
    );
};

export default MemberHedisTiles;
